import React, { useRef } from "react";
import axios from "axios";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { AutoComplete } from "primereact/autocomplete";
import { Dialog } from "primereact/dialog";
import { useSelectedClient } from '../../../hooks/useSelectedClient';
import '../../../styles/pages.css';

const US_STATES = [
  { label: 'Alabama', value: 'AL' }, { label: 'Alaska', value: 'AK' }, { label: 'Arizona', value: 'AZ' },
  { label: 'Arkansas', value: 'AR' }, { label: 'California', value: 'CA' }, { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' }, { label: 'Delaware', value: 'DE' }, { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' }, { label: 'Hawaii', value: 'HI' }, { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' }, { label: 'Indiana', value: 'IN' }, { label: 'Iowa', value: 'IA' },
  { label: 'Kansas', value: 'KS' }, { label: 'Kentucky', value: 'KY' }, { label: 'Louisiana', value: 'LA' },
  { label: 'Maine', value: 'ME' }, { label: 'Maryland', value: 'MD' }, { label: 'Massachusetts', value: 'MA' },
  { label: 'Michigan', value: 'MI' }, { label: 'Minnesota', value: 'MN' }, { label: 'Mississippi', value: 'MS' },
  { label: 'Missouri', value: 'MO' }, { label: 'Montana', value: 'MT' }, { label: 'Nebraska', value: 'NE' },
  { label: 'Nevada', value: 'NV' }, { label: 'New Hampshire', value: 'NH' }, { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' }, { label: 'New York', value: 'NY' }, { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' }, { label: 'Ohio', value: 'OH' }, { label: 'Oklahoma', value: 'OK' },
  { label: 'Oregon', value: 'OR' }, { label: 'Pennsylvania', value: 'PA' }, { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' }, { label: 'South Dakota', value: 'SD' }, { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' }, { label: 'Utah', value: 'UT' }, { label: 'Vermont', value: 'VT' },
  { label: 'Virginia', value: 'VA' }, { label: 'Washington', value: 'WA' }, { label: 'West Virginia', value: 'WV' },
  { label: 'Wisconsin', value: 'WI' }, { label: 'Wyoming', value: 'WY' }
];

export type AddCertificateProps = {
  visible: boolean;
  onHide: () => void;
};

export default function AddCertificate({ visible, onHide }: AddCertificateProps) {
    const toast = useRef<Toast>(null);
    const navigate = useNavigate();
  const { client: selectedClient } = useSelectedClient();
  const [holderName, setHolderName] = React.useState("");
  const [address1, setAddress1] = React.useState("");
  const [address2, setAddress2] = React.useState("");
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState<string | null>(null);
  const [zip, setZip] = React.useState("");
  const [lineOfBusiness, setLineOfBusiness] = React.useState("");
  const [additionalInterestRequested, setAdditionalInterestRequested] = React.useState(false);
  const [waverSubrogationRequested, setWaverSubrogationRequested] = React.useState(false);
  const [additionalRequirements, setAdditionalRequirements] = React.useState("");
  const [dueDate, setDueDate] = React.useState<Date | null>(null);
  const [description, setDescription] = React.useState("");
  const [policyNumber, setPolicyNumber] = React.useState("");
  const [agent, setAgent] = React.useState<any>(null);
  const [agentSuggestions, setAgentSuggestions] = React.useState<any[]>([]);
  const [policy, setPolicy] = React.useState<any>(null);
  const [policySuggestions, setPolicySuggestions] = React.useState<any[]>([]);
  const [addressSuggestions, setAddressSuggestions] = React.useState<any[]>([]);
  const [addressLoading, setAddressLoading] = React.useState(false);
  const address1Ref = React.useRef<HTMLInputElement>(null);
  const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);

  const handleAddress1Change = React.useCallback((value: string) => {
    setAddress1(value);
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    if (value.length > 2 && window.google && window.google.maps && window.google.maps.places) {
      setAddressLoading(true);
      debounceTimeout.current = setTimeout(() => {
        const autocompleteService = new window.google.maps.places.AutocompleteService();
        autocompleteService.getPlacePredictions(
          { input: value, types: ['address'] },
          (predictions: any[]) => {
            setAddressSuggestions(predictions || []);
            setAddressLoading(false);
          }
        );
      }, 300);
    } else {
      setAddressSuggestions([]);
      setAddressLoading(false);
    }
  }, []);

  const selectAddressSuggestion = React.useCallback((prediction: any) => {
    setAddress1(prediction.description);
    setAddressSuggestions([]);
    setAddressLoading(false);
    if (window.google && window.google.maps && window.google.maps.places) {
      const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
      placesService.getDetails({ placeId: prediction.place_id }, (place: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          fillAddressFields(place);
        }
      });
    }
  }, []);

  function fillAddressFields(place: any) {
    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let zip = '';

    if (place.address_components) {
      place.address_components.forEach((component: any) => {
        const types = component.types;
        if (types.includes('street_number')) {
          streetNumber = component.long_name;
        }
        if (types.includes('route')) {
          route = component.long_name;
        }
        if (types.includes('locality') || types.includes('administrative_area_level_3')) {
          city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          state = component.short_name;
        }
        if (types.includes('postal_code')) {
          zip = component.long_name;
        }
      });
    }

    setAddress1(place.formatted_address || `${streetNumber} ${route}`.trim());
    setCity(city);
    setState(state);
    setZip(zip);
  }

  React.useEffect(() => {
    if (!visible) return;
    // Load Google Maps API if not loaded
    if (!window.google || !window.google.maps) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, [visible]);

  const [submitting, setSubmitting] = React.useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClient) {
      toast.current?.show({ severity: 'error', summary: 'No Client', detail: 'No client selected. Please select a client first.', life: 4000 });
      return;
    }
    setSubmitting(true);
    try {
      // Compose payload as per API
      const payload = {
        holder_name: holderName,
        holder_address: address1,
        holder_city: city,
        holder_state: state,
        holder_zip: zip,
        policy_lob: lineOfBusiness,
        additional_interest_requested: additionalInterestRequested,
        waver_subrogation_requested: waverSubrogationRequested,
        additional_requirements: additionalRequirements,
        due_date: dueDate ? dueDate.toISOString().split('T')[0] : '',
        description,
        status: "0",
        insured_id: selectedClient.id,
        insured_name: selectedClient.name,
        insured_email: selectedClient.email,
        insured_phone: selectedClient.phone,
        policy_number: policyNumber,
        agents: agent ? [agent] : [],
        policies: policy ? [policy] : [],
      };
      const NCToken = localStorage.getItem('NCToken') || '';
      const igtoken = localStorage.getItem('igtoken') || '';
      await axios.post(
        `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/add-certificate`,
        payload,
        {
          headers: {
            'NCToken': NCToken,
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${igtoken}`,
          },
        }
      );
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Certificate request submitted successfully!', life: 3000 });
      setTimeout(() => {
        onHide();
      }, 1000);
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response && err.response.status === 401) {
        window.localStorage.clear();
        navigate('/');
        return;
      }
      const errorMsg = err.response?.data ? (typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data)) : err.message;
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to submit certificate request: ' + errorMsg, life: 4000 });
    } finally {
      setSubmitting(false);
    }
  };

  // Simulate agent/policy autocomplete
  const searchAgents = (e: any) => {
    setAgentSuggestions([
      { label: "Agent A", value: "agentA" },
      { label: "Agent B", value: "agentB" },
      { label: "Agent C", value: "agentC" },
    ].filter(a => a.label.toLowerCase().includes(e.query.toLowerCase())));
  };
  const searchPolicies = (e: any) => {
    setPolicySuggestions([
      { label: "Policy 1", value: "policy1" },
      { label: "Policy 2", value: "policy2" },
      { label: "Policy 3", value: "policy3" },
    ].filter(p => p.label.toLowerCase().includes(e.query.toLowerCase())));
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        header={<span style={{ color: '#0c0c0c', fontWeight: 600 }}>Request Certificate</span>}
        visible={visible}
        style={{ width: "900px", maxWidth: "98vw" }}
        onHide={onHide}
        modal
        className="addCertHolderDialog"
      >
        <form className="addCertHolderForm" onSubmit={handleSubmit}>
          <div className="page__muted" style={{ marginBottom: 4 }}>
            Enter details for the certificate request.
          </div>
          <div className="addCertHolderDialog__row2">
            <div className="addCertHolderDialog__col">
              <div className="pageCard" style={{ padding: 16 }}>
              <div style={{ color: '#111827', fontWeight: 500 }}>Holder Name <span style={{ color: '#dc2626' }}>*</span></div>
              <InputText value={holderName} onChange={e => setHolderName(e.target.value)} placeholder="Enter holder name" style={{ width: '100%', marginBottom: 16 }} />
              <div style={{ color: '#111827', fontWeight: 500 }}>Address Line 1 <span style={{ color: '#dc2626' }}>*</span></div>
              <InputText
                ref={address1Ref}
                value={address1}
                placeholder="Enter address line 1"
                onChange={e => handleAddress1Change(e.target.value)}
                style={{ width: '100%', marginBottom: 16 }}
              />
              {addressLoading && <div style={{ fontSize: '12px', color: '#666' }}>Loading suggestions...</div>}
              {addressSuggestions.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, border: '1px solid #ccc', maxHeight: '200px', overflowY: 'auto', background: 'white', position: 'absolute', zIndex: 1000, width: '100%' }}>
                  {addressSuggestions.map((suggestion, index) => (
                    <li
                      key={index}
                      onClick={() => selectAddressSuggestion(suggestion)}
                      style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f0f0f0')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                    >
                      {suggestion.description}
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ color: '#111827', fontWeight: 500 }}>Address Line 2</div>
              <InputText value={address2} onChange={e => setAddress2(e.target.value)} placeholder="Enter address line 2" style={{ width: '100%', marginBottom: 16 }} />
              <div style={{ color: '#111827', fontWeight: 500 }}>City <span style={{ color: '#dc2626' }}>*</span></div>
              <InputText value={city} onChange={e => setCity(e.target.value)} placeholder="Enter city" style={{ width: '100%', marginBottom: 16 }} />
              <div style={{ color: '#111827', fontWeight: 500 }}>State <span style={{ color: '#dc2626' }}>*</span></div>
              <Dropdown value={state} options={US_STATES} onChange={e => setState(e.value)} placeholder="Select state" showClear filter optionLabel="label" style={{ width: '100%', marginBottom: 16 }} emptyMessage="No available options" emptyFilterMessage="No available options" />
              <div style={{ color: '#111827', fontWeight: 500 }}>ZIP/Postal Code <span style={{ color: '#dc2626' }}>*</span></div>
              <InputText value={zip} onChange={e => setZip(e.target.value)} placeholder="Enter ZIP/postal code" style={{ width: '100%', marginBottom: 16 }} />
            </div>
          </div>
          <div className="addCertHolderDialog__col">
            <div className="pageCard" style={{ padding: 16 }}>
              <div style={{ color: '#111827', fontWeight: 500 }}>Line of Business <span style={{ color: '#dc2626' }}>*</span></div>
              <InputText value={lineOfBusiness} onChange={e => setLineOfBusiness(e.target.value)} placeholder="Enter line of business" style={{ width: '100%', marginBottom: 16 }} />
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <InputSwitch checked={additionalInterestRequested} onChange={e => setAdditionalInterestRequested(e.value ?? false)} />
                <span style={{ color: '#111827', fontWeight: 500, marginLeft: 8 }}>Additional Interest Requested</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <InputSwitch checked={waverSubrogationRequested} onChange={e => setWaverSubrogationRequested(e.value ?? false)} />
                <span style={{ color: '#111827', fontWeight: 500, marginLeft: 8 }}>Waver of Subrogation Requested</span>
              </div>
              <div style={{ color: '#111827', fontWeight: 500 }}>Due Date</div>
              <Calendar value={dueDate} onChange={e => setDueDate(e.value as Date)} placeholder="Select due date" showIcon dateFormat="mm/dd/yy" style={{ width: '100%', marginBottom: 16 }} />
              <div style={{ color: '#111827', fontWeight: 500 }}>Policy Number</div>
              <InputText value={policyNumber} onChange={e => setPolicyNumber(e.target.value)} placeholder="Enter policy number" style={{ width: '100%', marginBottom: 16 }} />
              <div style={{ color: '#111827', fontWeight: 500 }}>Agents</div>
              <AutoComplete value={agent} suggestions={agentSuggestions} completeMethod={searchAgents} onChange={e => setAgent(e.value)} field="label" placeholder="Search agent..." className="w-100" style={{ width: '100%', marginBottom: 16 }} />
              <div style={{ color: '#111827', fontWeight: 500 }}>Policies</div>
              <AutoComplete value={policy} suggestions={policySuggestions} completeMethod={searchPolicies} onChange={e => setPolicy(e.value)} field="label" placeholder="Search policy..." className="w-100" style={{ width: '100%', marginBottom: 16 }} />
            </div>
          </div>
        </div>
        <div className="addCertHolderDialog__row2">
          <div className="addCertHolderDialog__col">
            <div className="pageCard" style={{ padding: 16 }}>
              <div style={{ color: '#111827', fontWeight: 500 }}>Additional Requirements</div>
              <InputTextarea value={additionalRequirements} onChange={e => setAdditionalRequirements(e.target.value)} placeholder="Enter additional requirements" style={{ width: '100%', marginBottom: 16 }} rows={3} />
            </div>
          </div>
          <div className="addCertHolderDialog__col">
            <div className="pageCard" style={{ padding: 16 }}>
              <div style={{ color: '#111827', fontWeight: 500 }}>Description</div>
              <InputTextarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Enter description" style={{ width: '100%', marginBottom: 16 }} rows={3} />
            </div>
          </div>
        </div>
        <div className="addCertHolder__actions">
          <Button label="Cancel" className="p-button-text appButton--secondary" type="button" onClick={onHide} disabled={submitting} />
          <Button label={submitting ? "Submitting..." : "Submit"} className="appButton--primary" type="submit" disabled={submitting} />
        </div>
        </form>
      </Dialog>
    </>
  );
}
